/**
 * Inline name editor for room tags — a floating input positioned over the
 * tag's anchor in the viewport, tracked against the camera each frame.
 *
 * Opened by the `room_tag_edit` scene event: the RoomTagTool dispatches it
 * when an existing tag label is clicked, and the tag-creation handler right
 * after a new tag is placed (default name pre-filled and fully selected, so
 * typing replaces it). Opening also selects the tag's group in the left
 * panel. Enter or clicking anywhere outside commits the rename; Escape
 * cancels. New tags carry a `ready` promise the rename awaits so it can't
 * reach the server before the folder's create does.
 */

import { memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useApolloClient } from '@apollo/client/react';
import * as THREE from 'three';
import { RendererContext } from '../../contexts/RendererContext';
import { toggleSelectedAnnotation } from '../../redux/rendererReducer';
import { useProjectUpdateEntityNameMutation } from '../../types/graphqlTypes';
import { renameGroupInCache } from '../../hooks/potree/useGroupOperations';
import { useToastNotifications, ToastType } from '../../hooks/useToastNotifications';
import { useT } from '../../translation/src';
import type { RoomTagEditRequest } from '../../utils/roomFolders';

const RoomTagNameEditor_: React.FC = () => {
  const { viewer } = useContext(RendererContext);
  const { projectId = '' } = useParams();
  const dispatch = useDispatch();
  const apolloClient = useApolloClient();
  const [updateEntityName] = useProjectUpdateEntityNameMutation();
  const { showToast } = useToastNotifications();
  const roomTagNotCreatedTitle = useT('Room tag not created');
  const roomTagNotCreatedText = useT('Creating the room tag failed, so the name was not saved');
  const [request, setRequest] = useState<RoomTagEditRequest | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Open on the scene event + select the tag in the left panel. A plain
  // `room_tag_select` (single click on a tag) only selects, no editor.
  useEffect(() => {
    if (!viewer) return;
    const select = (folderId: string) => dispatch(toggleSelectedAnnotation({ identifier: folderId, type: 'group', clearOtherSelections: true }));
    const onEdit = (event: { value?: RoomTagEditRequest }) => {
      if (!event.value) return;
      setRequest(event.value);
      select(event.value.folderId);
    };
    const onSelect = (event: { value?: { folderId: string } }) => {
      if (event.value) select(event.value.folderId);
    };
    viewer.scene.addEventListener('room_tag_edit', onEdit);
    viewer.scene.addEventListener('room_tag_select', onSelect);
    return () => {
      viewer.scene.removeEventListener('room_tag_edit', onEdit);
      viewer.scene.removeEventListener('room_tag_select', onSelect);
    };
  }, [viewer, dispatch]);


  const commit = useCallback(() => {
    if (!request) return;
    const name = inputRef.current?.value.trim() ?? '';
    setRequest(null);
    if (!projectId || !name || name === request.name) return;
    // Optimistic: panel row + viewport label rename instantly. When the
    // folder's create ends up failing, createGroup's failure refetch wipes
    // this together with the phantom folder.
    renameGroupInCache(apolloClient, projectId, request.folderId, name);
    void Promise.resolve(request.ready ?? true)
      .then((created) => {
        if (!created) {
          // The folder never made it to the server — renaming it would
          // target a nonexistent group. Tell the user instead of silence.
          showToast({ title: roomTagNotCreatedTitle, text: roomTagNotCreatedText, type: ToastType.WARNING });
          return undefined;
        }
        return updateEntityName({ variables: { projectId, name, groupIdentifier: request.folderId } });
      })
      .catch(() => undefined);
  }, [request, projectId, updateEntityName, apolloClient, showToast, roomTagNotCreatedTitle, roomTagNotCreatedText]);

  // Mirror the panel selection onto the viewport labels (accent tint).
  const selectedGroupIds = useSelector((state: { rendererProvider: { selectedAnnotations: { identifier: string; type: string }[] } }) =>
    state.rendererProvider.selectedAnnotations
      .filter((a) => a.type === 'group')
      .map((a) => a.identifier)
      .join(','),
  );
  useEffect(() => {
    viewer?.roomLabelsLayer?.setSelectedFolders(selectedGroupIds ? selectedGroupIds.split(',') : []);
  }, [viewer, selectedGroupIds]);

  // Hide the tag's own label while the editor floats over it.
  useEffect(() => {
    if (!request || !viewer) return;
    viewer.roomLabelsLayer?.setSuppressedFolder(request.folderId);
    return () => viewer.roomLabelsLayer?.setSuppressedFolder(null);
  }, [request, viewer]);

  // Clicking anywhere outside the editor commits — the canvas may swallow
  // focus changes (preventDefault on mousedown), so blur alone isn't enough.
  useEffect(() => {
    if (!request) return;
    const onPointerDown = (event: PointerEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      commit();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [request, commit]);

  // Follow the anchor's projected screen position while open. The first
  // position is applied SYNCHRONOUSLY so the container is visible before the
  // focus effect runs (focus fails on hidden elements).
  useEffect(() => {
    if (!request || !viewer) return;
    const world = new THREE.Vector3(request.position.x, request.position.y, request.position.z);
    let raf = 0;
    const position = () => {
      const el = containerRef.current;
      const rect = viewer.renderer.domElement.getBoundingClientRect();
      if (el && rect.width > 0 && rect.height > 0) {
        const projected = world.clone().project(viewer.scene.getActiveCamera());
        el.style.left = `${(projected.x * 0.5 + 0.5) * rect.width}px`;
        el.style.top = `${(-projected.y * 0.5 + 0.5) * rect.height}px`;
        el.style.visibility = projected.z > 1 ? 'hidden' : 'visible';
      }
    };
    const tick = () => {
      position();
      raf = requestAnimationFrame(tick);
    };
    position();
    // Now that the container is visible, grab focus with the whole current
    // name selected so typing replaces it. (focus() is a silent no-op while
    // the container is still visibility:hidden — the pre-position state.)
    inputRef.current?.focus();
    inputRef.current?.select();
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [request, viewer]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commit();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setRequest(null);
      }
      // Keep viewer shortcuts (tool hotkeys, delete, …) out of typing.
      event.stopPropagation();
    },
    [commit],
  );

  if (!request) return null;

  return (
    <div ref={containerRef} className="absolute z-20 transform -translate-x-1/2 -translate-y-1/2" style={{ visibility: 'hidden' }}>
      <input
        key={request.folderId}
        ref={inputRef}
        defaultValue={request.name}
        onKeyDown={onKeyDown}
        onBlur={commit}
        className="w-40 rounded border border-neon-green-500 bg-white/90 px-2 py-1 text-center text-sm text-black shadow focus:outline-none dark:bg-eerie-black/90 dark:text-white"
      />
    </div>
  );
};

export const RoomTagNameEditor = memo(RoomTagNameEditor_);
